<template lang="pug">
.updatebilling-container(:class="{ 'opacity-50 pointer-events-none': updating }")
  b(v-if="!user") 请登录或注册账号
  div(v-else)
    div.center.flex.flex-col.gap-y-4
      div(v-if="props.showTitle")
        h3 {{ $t('update_billing.header') }}
        TooltipProvider
          Tooltip
            TooltipTrigger(as="h5")
              h5.flex.items-center.gap-1
                Icon(name="mdi:help-circle" class="w-4 h-4")
                span {{ $t('update_billing.why') }}
            TooltipContent.max-w-xs
              | {{ $t('update_billing.sub.a') }}
      form.w-full(@submit.prevent :disabled="!isAdmin && suspended")
        div.gap-y-3.w-full
          div.grid.grid-cols-2.gap-2
            div
              Label(for="firstname") {{ $t('update_billing.form.firstname') }}{{ personalNameRequired }}
              Input(
                id="firstname"
                class="bg-[var(--background-color-pure)]"
                v-model="newbilling.firstname"
                type="text"
                :placeholder="$t('update_billing.form.firstname')"
                :aria-required="isPersonalNameRequired"
              )
            div
              Label(for="lastname") {{ $t('update_billing.form.lastname') }}{{ personalNameRequired }}
              Input(
                id="lastname"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.lastname'
                type="text"
                :placeholder="$t('update_billing.form.lastname')"
                :aria-required="isPersonalNameRequired"
              )
          div
            Label(for="addr") {{ $t('update_billing.form.addr') }}
            Input(
              id="addr"
              class="bg-[var(--background-color-pure)]"
              v-model='newbilling.addr'
              type="text"
              :placeholder="$t('update_billing.form.addr')"
              aria-required="true"
            )
          div.grid.grid-cols-2.gap-2
            div
              Label(for="city") {{ $t('update_billing.form.city') }}
              Input(
                id="city"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.city'
                type="text"
                :placeholder="$t('update_billing.form.city')"
                aria-required="true"
              )
            div
              Label(for="state") {{ $t('update_billing.form.state') }}
              Input(
                id="state"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.state'
                type="text"
                :placeholder="$t('update_billing.form.state')"
              )
          div.grid.grid-cols-2.gap-2
            div
              Label(for="zip") {{ $t('update_billing.form.zip') }}
              Input(
                id="zip"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.zip'
                type="text"
                :placeholder="$t('update_billing.form.zip')"
              )
            div
              Label(for="country")
                .flex.justify-between.items-center
                  span
                    | {{ $t('update_billing.form.country') }}
                  span.eu-hint(v-if="isEuCountry")
                    span.warning(v-if="hasVerifiedVatReverseCharge") {{ $t('billing.eu_hint_reverse_charged') }}
                    span.warning(v-else-if="isPendingVatReverseCharge") {{ $t('billing.eu_hint_reverse_charge_pending') }}
                    span.warning(v-else) {{ $t('billing.eu_hint') }}
              Select(
                v-model="newbilling.country"
                @update:modelValue="onCountryChange"
              )
                SelectTrigger(
                  class="bg-[var(--background-color-pure)]"
                )
                  SelectValue(:placeholder="$t('update_billing.form.country')")
                SelectContent
                  SelectItem(v-for='item in countryList', :key='item', :value='item')
                    | {{ item }}
          div(v-if="newbilling.company_name || inputCompanyInfo")
            div
              Label(for="company_name") {{ $t('update_billing.form.company_name') }}*
              Input(
                id="company_name"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.company_name'
                type="text"
                :placeholder="$t('update_billing.form.company_name')"
                aria-required="true"
              )
            div.mt-3
              Label(for="company_vat") {{ $t('update_billing.form.company_vat') }}
              Input(
                id="company_vat"
                class="bg-[var(--background-color-pure)]"
                v-model='newbilling.company_vat'
                type="text"
                :placeholder="$t('update_billing.form.company_vat')"
              )

        //- p {{ $t('billing.eu_vat_prefix') }}{{ vatRate }}%{{ vatReverseCharge ? $t('billing.eu_vat_reverse_charge') : '' }}

    div
      div.buttons
        Button.full-width.back-button.save(type='primary' @click='updateBilling()' :disabled="!isAdmin && suspended" :loading="updating") {{ $t('update_billing.save') }}
        div.extra-buttons.flex.justify-between.items-center.mt-2
          div
            Button.font-normal.p-1(variant="link" @click='toggleCompanyFields()' v-if="!hasCompanyInfo")
              Icon(name="material-symbols-light:business-center-outline-rounded" class="mr-1")
              | {{ $t('billing.input_company_info') }}
            Button.secondary.font-normal.p-1(variant="link" @click='toggleCompanyFields()' v-if="inputCompanyInfo") {{ $t('billing.cancel_company_info') }}
          div
            Button.secondary.font-normal.p-1(variant="link" @click="delBilling()" :disabled="!isAdmin && suspended")
              Icon(name="majesticons:trash-line" class="mr-1")
              | {{ $t('update_billing.del') }}
      div.read-from-other-account(v-if="isAdmin")
        div.flex.gap-2.items-center
          Input.flex-1(class="bg-[var(--background-color-pure)]" v-model.number="userIdTocopy" type="number" placeholder="要读取账单信息的用户ID" @keyup.enter.native="readBillingAndSet")
          div.flex.gap-2
            Button.p-1(variant="link" @click="readBillingAndSet") {{ $t('update_billing.copy') }}
            Button.p-1(variant="link" @click="clearBilling") {{ $t('update_billing.clear') }}
          div.flex.items-center.border-l.border-border.pl-3.ml-1
            Button.p-1(variant="link" @click="loadLastPaymentBilling" :disabled="loadingLastPaymentBilling")
              Icon(name="lucide:history" class="mr-1")
              | {{ $t('update_billing.load_last_payment') }}
</template>

<script lang="ts" setup>
import { toast } from '~/utils/sonner'
import { useBillingForm, BILLING_FIELDS } from '@/composables/useBillingForm'
import type { LastPaymentBillingResponse, BillingFormProps, UserBillingInfo, BillingAddress } from '@/types/billing'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'

import countryList from '@/assets/data/countryList.json'

const props = defineProps<BillingFormProps>()
const notify = (input: Parameters<typeof toast>[0]) => {
  if (!props.toastPosition) {
    return toast(input)
  }
  if (typeof input === 'string') {
    return toast({ title: input, position: props.toastPosition })
  }
  return toast({ ...input, position: input.position ?? props.toastPosition })
}

const $utils = useUtils()
const $t = useI18n().t
const user = useUser()
const session = useVisitorSession()
const suspended = computed(() => user.extra?.suspend ?? false)
const isAdmin = computed(() => session.isAdmin)

const userIdTocopy = ref<number | undefined>(undefined)
const loadingLastPaymentBilling = ref(false)

// Use the billing form composable
const {
  formData: newbilling,
  isUpdating: updating,
  showCompanyFields: inputCompanyInfo,
  isPersonalNameRequired,
  hasCompanyInfo,
  isEuCountry,
  vatRate,
  vatReverseCharge,
  hasVerifiedVatReverseCharge,
  isPendingVatReverseCharge,
  isFormEmpty: isEmptyBilling,
  validateForm,
  resetForm,
  loadBillingData,
  prepareBillingForSubmit,
  toggleCompanyFields,
} = useBillingForm({
  initialData: user?.billing
})

const personalNameRequired = computed(() => {
  return isPersonalNameRequired.value ? '*' : ''
})
// Initialize billing data immediately, not in onMounted
// This prevents showing escaped data briefly
if (user?.billing?.country) {
  loadBillingData(user.billing)
}

onMounted(() => {
  // Additional initialization if needed
})
type UpdateBillingOptions = {
  silentSuccess?: boolean
}

async function updateBilling(options: UpdateBillingOptions = {}) {
  const validation = validateForm()
  if (!validation.valid) {
    notify({
      title: $t('update_billing.incomplete'),
      variant: 'destructive',
    })
    return false
  }

  if (!countryList.includes(newbilling.value.country)) {
    notify({
      title: 'Unrecognized country name, please select again.',
      variant: 'destructive',
    })
    return false
  }

  try {
    updating.value = true
    const billingData = prepareBillingForSubmit()
    await user.updateBilling({ billing: billingData })
    if (!options.silentSuccess) {
      notify({ title: $t('update_billing.saved') })
    }
    return true
  } catch (error) {
    const errorData = error as { data?: { message?: string } }
    notify({
      title: $t(`errors.${errorData?.data?.message}.title`),
      description: $t(`errors.${errorData?.data?.message}.message`),
      variant: 'destructive',
    })
    return false
  } finally {
    updating.value = false
  }
}
async function delBilling() {
  try {
    const ret = await useDialog().confirm({
      title: $t('warning.title'),
      body: $t('warning.confirm_delete'),
      confirmButtonText: $t('del'),
      cancelButtonText: $t('cancel'),
      confirmButtonVariant: 'destructive',
    })
    if (ret !== 'confirm') {
      return
    }
    await user.updateBilling({ billing: null })
    resetForm()
    notify({ title: $t('update_billing.deleted'), variant: 'default' })
  } catch (error) {
    const errorData = error as { data?: { message?: string } }
    notify({
      title: $t(`errors.${errorData?.data?.message}.title`),
      description: $t(`errors.${errorData?.data?.message}.message`),
      variant: 'destructive',
    })
  }
}
async function readBillingAndSet() {
  if (!userIdTocopy.value) {
    return notify({
      title: '请输入用户ID',
      variant: 'destructive',
    })
  }

  try {
    const readUserId = Number(userIdTocopy.value)
    const readUserInfo = await user.getUserInfo({ id: readUserId })

    if (!readUserInfo?.billing) {
      return notify({
        title: '该用户没有账单信息',
        variant: 'destructive',
      })
    }

    loadBillingData(readUserInfo.billing)
    notify({ title: `已载入用户 ${readUserId} 的账单信息` })
  } catch (error) {
    console.error('Failed to read billing', error)
    notify({
      title: '读取账单信息失败',
      variant: 'destructive',
    })
  }
}

async function loadLastPaymentBilling() {
  try {
    loadingLastPaymentBilling.value = true
    const data: LastPaymentBillingResponse = await user.getLastPaymentBilling()

    if (!data.billing) {
      return notify({
        title: data.message || $t('update_billing.no_payment_billing'),
        variant: 'destructive',
      })
    }

    loadBillingData(data.billing)
    const paymentInfo = [
      data.payment_id ? `#${data.payment_id}` : '',
      data.payment_channel,
      data.payment_date ? $utils.dateStr(data.payment_date) : '',
    ].filter(Boolean).join(' ')

    notify({
      title: $t('update_billing.loaded_from_payment'),
      description: paymentInfo || undefined,
    })
  } catch (error) {
    console.error('Failed to load last payment billing', error)
    notify({
      title: $t('update_billing.load_error'),
      variant: 'destructive',
    })
  } finally {
    loadingLastPaymentBilling.value = false
  }
}

function clearBilling() {
  resetForm()
  notify({ title: '已清空账单信息' })
}

function onCountryChange(value: string) {
  // Handle country change if needed
  // The composable already handles VAT logic
}

defineExpose({
  updateBilling,
})
</script>

<style lang="postcss" scoped>
@reference "@/assets/css/tailwind.css";
.updatebilling-container {
  width: 100%;
  /* padding: 20px; */
  min-height: 350px;
  text-align: center;
  /* max-width: 600px; */
  margin: 0 auto;

  @media (max-width: 768px) {
    /* padding: 10px; */
  }

  .center {
    /* max-width: 600px; */
    margin: 0 auto;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  h5 {
    font-size: 1rem;
    color: var(--secondary-color);
    cursor: help;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .eu-hint {
    /* margin-top: 1rem; */
    /* padding: 0rem; */
    /* background: rgba(255, 193, 7, 0.1); */
    /* border-radius: 0.5rem; */

    .warning {
      /* color: var(--warning-color); */

      @apply ml-4 text-xs;
    }
  }

  .buttons {
    margin-top: 2rem;

    .save {
      font-weight: 600;
    }

    .extra-buttons {
      margin-top: 0.5rem;
    }
  }

  .read-from-other-account {
    /* margin-top: 2rem; */
    padding-top: 1rem;

    h5 {
      margin: 0.5rem 0;
    }
  }

  /* Form styles */
  form {
    text-align: left;

    label {
      display: block;
      margin-bottom: 0.25rem;
      font-size: 0.875rem;
      color: var(--secondary-color);
    }
  }

  .full-width {
    width: 100%;
  }

  .pr-1 {
    padding-right: 0.25rem;
  }
}
</style>
